import moment from "moment";
import React, { useState } from "react";
import OptionalTextAreaInput from "components/ValidatedFields/OptionalTextAreaInput";
import { FormRow } from "../components";
import RowType from "./rowtype";
import TimeRange from "./timerange";
import { useCreateTimetable, useEditTimetable } from "./api";
interface Props {
  row?: server.dto.TimetableEntry
  onClose: () => void
  showId: number
}

const maxNotesLength = 100;

const getMomentFromDate = (date: Date | undefined) => {
  if (date) {
    return moment.utc(date);
  }

  return undefined;
};

const EditRow = (props: Props) => {
  const [startTime, setStartTime] = useState(props.row ? getMomentFromDate(props.row.startTime) : null);
  const [endTime, setEndTime] = useState(props.row ? getMomentFromDate(props.row.endTime) : null);
  const [isNextDay, setIsNextDay] = useState(props.row?.nextDay ?? false);
  const [type, setType] = useState(props.row?.type ?? { value: undefined, text: undefined });
  const [notes, setNotes] = useState(props.row?.notes || "");

  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [noTypeSelectedError, setNoTypeSelectedError] = useState(false);
  const [customNameIsEmptyError, setCustomNameIsEmptyError] = useState(false);

  const hasOnlyEndTime = startTime == null && endTime != null;

  const useCreateTimetableEntry = useCreateTimetable();
  const useEditTimetableEntry = useEditTimetable();

  const onRowTypeChange = (newType: server.dto.TimetableEntryType) => {
    setType(newType);
    setNoTypeSelectedError(false);
    setCustomNameIsEmptyError(false);
  };

  const onStartTimeChange = (begin: moment.Moment) => {
    if (begin != null) {
      const isEarlyMorning = (begin.hour() >= 0) && (begin.hour() <= 7) && (begin.valueOf() !== 0);
      const willBeNextDay = isEarlyMorning ? true : isNextDay;

      setIsNextDay(willBeNextDay);
    }
    setStartTime(begin);
  };

  const onSave = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    setErrorMessage(undefined);
    if (hasOnlyEndTime) {
      return;
    }

    if (type.value === undefined) {
      setNoTypeSelectedError(true);
      return;
    }

    if (type.value === "custom" && type.text.trim().length === 0) {
      setCustomNameIsEmptyError(true);
      return;
    }

    const isNewRow = !props.row;
    let outcome: server.dto.ApiResponse;
    if (isNewRow) {
      outcome = await useCreateTimetableEntry.mutateAsync({
        showId: props.showId,
        startTime: startTime?.toDate(),
        endTime: endTime?.toDate(),
        customText: type.text,
        type: type.value,
        nextDay: isNextDay,
        notes
      }).then(r => r.data);
    } else {
      outcome = await useEditTimetableEntry.mutateAsync({
        timetableEntryId: props.row.timetableEntryId,
        startTime: startTime?.toDate(),
        endTime: endTime?.toDate(),
        customText: type.text,
        type: type.value,
        nextDay: isNextDay,
        notes
      }).then(r => r.data);
    }

    if (outcome.hasErrors) {
      setErrorMessage(outcome.errors.map(m => m.message).join(", "));
    } else {
      props.onClose();
    }
  };

  const isLoading = useCreateTimetableEntry.isLoading || useEditTimetableEntry.isLoading;
  const hasError = useCreateTimetableEntry.isError || useEditTimetableEntry.isError || useEditTimetableEntry.data?.data?.hasErrors || useCreateTimetableEntry.data?.data?.hasErrors;
  return <>
    <form>
      <div className="grow1 flex-column gap">

        <FormRow label={RESX.DateTime.Time}>
          <TimeRange
            start={startTime}
            end={endTime}
            nextDay={isNextDay}
            onStartTimeChanged={onStartTimeChange}
            onEndTimeChanged={setEndTime}
            onNextDayChanged={setIsNextDay}
            startTimeError={hasOnlyEndTime}
          />
        </FormRow>

        <FormRow label={RESX.GeneralLabels.Type}>
          <RowType
            dropdownError={noTypeSelectedError}
            emptyCustomNameError={customNameIsEmptyError}
            value={type}
            onChange={onRowTypeChange}
          />
        </FormRow>

        <FormRow label={RESX.Note.Notes}>
          <OptionalTextAreaInput
            maxLength={maxNotesLength}
            rows={3}
            value={notes}
            onChange={setNotes}
          />
        </FormRow>
      </div>

      <div className="flex gap margin-top-bigger">
        <div className="grow1" style={{ color: "red" }}>{hasError && (errorMessage || RESX.GeneralLabels.SomethingUnexpectedHappened)}</div>
        <div>
          <button
            type="submit"
            disabled={isLoading}
            className="button-new primary"
            onClick={onSave}>
            {RESX.Buttons.btnSave}
          </button>
        </div>
      </div>
    </form >
  </>;
};

export default EditRow;
